<template>
  <Callout
    v-if="coverageWarnings?.length || numberUnmatchedHoldings > 0 || csvFiles.length"
    status="primary"
  >
    <div class="tw-flex tw-justify-center stk-p-2">
      <div class="tw-w-full tw-max-w-7xl tw-px-4 tw-flex tw-items-center tw-gap-5">
        <i class="fa fa-info-circle tw-text-2xl" />
        <div class="tw-w-full">
          <p class="tw-text-sm">
            Some of your holdings are either individual bonds or unmatched.
            Currently, YourStake's returns, risk, and volatility calculations only have coverage for matched equities and funds.
            Now, for individual bonds or unmatched/non-publicly traded securities, you can use the data from another ticker for your analytics by clicking on the holding and selecting an alternative ticker.
            For example, you can select "MUB" (a muni bond ETF) as a proxy for an individual muni bond.
            <br><br>
          </p>
          <Stack>
            <Stack
              class="tw-items-center"
              gap="2"
              v-if="numberUnmatchedHoldings > 0"
              
            >
              <p class="tw-text-sm">
                You can also click the button below to update unmatched holdings with the best-match ticker in our database based on the holding name. The search includes all security types except bonds.
              </p>
              <o-tooltip
                variant="primary"
                :active="!loading || (csvFiles.length == 0)"
                :label="`Click to find missing tickers`"
              >
                <Button
                  variant="primary"
                  :disabled="loading || noTickerMatches || csvFiles.length > 0"
                  @click="loadMagicTicker"
                >
                  <span v-if="loading">
                    <i class="fa fa-spinner fa-spin tw-text-xl tw-mr-1" />
                    Searching for ticker-matches
                  </span>
                  <span v-else>
                    Magic Ticker Fill
                  </span>
                </Button>
              </o-tooltip>
            </Stack>
            <Stack
              gap="2"
              v-if="csvFiles.length"
              class="tw-flex tw-flex-col tw-gap-1"
            >
              <p class="tw-text-sm">
                <span class="tw-font-semibold tw-text-sm">
                  Spreadsheets to download. Please take a look at the notes column for information about the ticker matches.
                </span>
              </p>
              <a
                v-for="file in csvFiles"
                :key="file?.filename"
                :href="file?.url"
                class="tw-text-sm"
              >
                Spreadsheet for {{ file?.filename }}
              </a>
            </Stack>
            <Stack
              gap="2"
              v-else-if="noTickerMatches"
              class="tw-flex tw-flex-col tw-gap-1"
            >
              <p class="tw-text-sm">
                <span class="tw-font-semibold tw-text-sm">
                  {{ noMatchMessage }}
                </span>
              </p>
            </Stack>
          </Stack>
        </div>
      </div>
    </div>
  </Callout>
</template>

<script>
import { mapStores } from 'pinia';
import { useCheckupStore } from '@frontend/ui/stores/checkupStore';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore.js';
import { postSendInternalEmail } from '@frontend/api/emailsApi.js';
import { newWorkbook, getWorkbookURL } from '@frontend/shared/exceljs/helpers';
import { Button, Callout, Stack } from '@stakeui';

export default {
  name: 'MagicTickerFill',
  props: {
    coverageWarnings: {
      type: Array,
      default: () => []
    },
    numberUnmatchedHoldings: {
      type: Number,
      default: 0
    }
  },
  components: {
    Button,
    Callout,
    Stack,
  },
  data() {
    return {
      loading: false,
      csvFiles: [],
      noTickerMatches: false,
      no_match_reasons: []
    };
  },
  computed: {
    ...mapStores(useCheckupStore),
    ...mapStores(useAdvisorStore),
    headers() {
      return {
        'X-CSRFToken': this.$root.$data.csrfToken,
      };
    },
    noMatchMessage() {
      if (this.noTickerMatches) {
        if (this.no_match_reasons.some(reason => reason.includes('MTF did not find matches'))) {
          return 'Magic-Ticker-Fill did not find any matches. The YourStake team has been notified.';
        } else if (this.no_match_reasons.some(reason => reason.includes('0 unmatched for MTF'))) {
          return 'No unmatched securities that are not Bonds or Other. Magic-ticker-fill not run. Please use edit-after-upload.';
        } else if (this.no_match_reasons.some(reason => reason.includes('cannot run MTF'))) {
          return 'Not enough information for Magic-ticker-fill. Please use edit-after-upload.';
        }
      }
      return 'Magic-Ticker-Fill did not find any ticker matches. Someone from the YourStake team will reach out to you within one business day. You can still add tickers yourself by editing the table below.';
    }
  },
  methods: {
    async loadMagicTicker() {
      this.loading = true; // Activate spinner
      try {
        const baseUrl = '/api/v1/get-magic-ticker-fill/';
        const url = new URL(baseUrl, window.location.origin);

        const portfolioIdMap = new Map(); // Create a map to track portfolio IDs so we know what to update

        // Check if portfolios are available
        if (this.checkupStore?.selectedPortfolios && this.checkupStore.selectedPortfolios.length > 0) {
          this.checkupStore.selectedPortfolios.forEach(portfolio => {
            if (portfolio.external_id !== undefined && portfolio.external_id !== null) {
              url.searchParams.append('portfolio_external_id', portfolio.external_id);
              portfolioIdMap.set(portfolio.external_id, portfolio.name); // Store the portfolio ID and name
            }
          });
        }

        // Trigger the background task
        const response = await fetch(url);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'An error occurred, the YourStake team has been notified.');
        }

        // Check if tasks are returned properly
        if (!data.tasks || !Array.isArray(data.tasks)) {
          throw new Error('No tasks were returned from the server.');
        }

        // Store the task IDs
        const taskIds = data.tasks.map(task => task.task_id);

        // get the task status
        const results = await this.pollMTFTaskStatus(taskIds);
        console.log('MTF RESULTS', results);

        if (results.length === 0 ) {
          // Handle the case where no data was found-- this happens when someone tries to match MTF on something where
          // the only unmatched holdings are bonds or other
          this.noTickerMatches = true;
          this.$buefy.toast.open({
            duration: 5000,
            message: 'Insufficient information for Magic-ticker-fill. Use edit-after-upload to assign tickers.',
            type: 'is-info',
          });
        } else {
          // Process the returned data
          const newFiles = [];
          const updatedPortfolioIds = [];

          // allow user to download updated csv files
          for (const result of results) {
            if (result.data.csv) {
              const holdings = JSON.parse(result.data.csv);
              // Format the csv data to holdings spreadsheet with MTF notes
              const createdCsvData = await this.createSpreadsheet(result.data.name, holdings);
              if (createdCsvData) {
                newFiles.push(createdCsvData);
                updatedPortfolioIds.push(result.data.portfolioID);
              }
            }
            if (result.data.no_match_reason) {
              const no_match_reason = result.data.no_match_reason;
              this.no_match_reasons.push(no_match_reason);
            }
          }
          console.log('MTF no match reasons', this.no_match_reasons);

          if (this.no_match_reasons.length == results.length) {
            // Handle the case where MTF was run but no matches were found
            this.noTickerMatches = true;

            // Check the conditions for the messages
            if (this.no_match_reasons.some(reason => reason.includes('MTF did not find matches'))) {
              this.$buefy.toast.open({
                duration: 5000,
                message: 'Magic-ticker-fill did not find any matches. The YourStake team has been notified.',
                type: 'is-info',
              });
            } else if (this.no_match_reasons.some(reason => reason.includes('0 unmatched for MTF'))) {
              this.$buefy.toast.open({
                duration: 5000,
                message: 'No unmatched securities that are not Bonds or Other. Magic-ticker-fill not run. Please use edit-after-upload.',
                type: 'is-info',
              });
            } else if (this.no_match_reasons.some(reason => reason.includes('cannot run MTF'))) {
              this.$buefy.toast.open({
                duration: 5000,
                message: 'Not enough information for Magic-ticker-fill. Please use edit-after-upload.',
                type: 'is-info',
              });
            }
          }

          this.csvFiles = newFiles;
          
          // Emit event to notify parent
          this.$emit('mtf-files-updated', newFiles);

          // update the holdings for portfolios that were changed, so that the holdings table will update (reactive propty)
          if (updatedPortfolioIds.length) {
            updatedPortfolioIds.forEach(id => {
              this.checkupStore.updateHoldingsByPortfolio(id);
            });
            
            // Notify parent that holdings were updated
            this.$emit('holdings-updated');
          }
        }
      } catch (error) {
        console.error('Failed to run magic-ticker-fill:', error);
        const template = 'magic_ticker_fill_error';
        postSendInternalEmail(`portfolio_checkup/error/${template}.yaml`);
        this.$buefy.toast.open({
          duration: 30000,
          message: 'Failed to run magic-ticker-fill. The YourStake team has been notified.',
          type: 'is-danger',
        });
      } finally {
        this.loading = false; // Deactivate spinner regardless of outcome
      }
    },

    async pollMTFTaskStatus(taskIds) {
      const checkUrl = '/api/v1/check-magic-ticker-fill-status/';
      const pollInterval = 5000; // check task status every 5 seconds

      const results = [];

      // keep checking status until all tasks are done
      // if MTF is run on multiple portfolios, there is a task for each portfolio
      while (taskIds.length > 0) {
        await this.sleep(pollInterval);
        // get the results from the task
        const responses = await Promise.all(taskIds.map(async taskId => {
          const response = await fetch(`${checkUrl}${taskId}`);
          const data = await response.json();
          return { taskId, ...data };
        }));

        for (const res of responses) {
          if (res.status === 'complete' || res.status === 'failed') {
            console.log('complete/failed task results', res);
            results.push(res);
            taskIds = taskIds.filter(id => id !== res.taskId);
          }
        }
      }

      return results;
    },
    
    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },
    
    async createSpreadsheet(filename, holdings, activityData = null) {
      if (!holdings?.length) return null;
      console.log('Creating spreadsheet for ' + filename + ' with ' + holdings.length + ' holdings in MagicTickerFill component.');
      
      let workbook = newWorkbook();
      
      // Create the main holdings worksheet
      workbook = this.createHoldingsWorksheet(workbook, holdings);
      
      // Create custom holdings worksheet if format exists for this advisor
      workbook = this.createCustomHoldingsWorksheet(workbook, holdings);
      
      // Add activity worksheet if data exists
      if (activityData && activityData.length > 0) {
        console.log('Adding Activity Data to spreadsheet', activityData.length);
        workbook = this.createActivityWorksheet(workbook, activityData);
      }
      
      // Gets the download url for the workbook
      const url = await getWorkbookURL(workbook);
      
      return { filename: `${filename}.xlsx`, url };
    },
    
    createHoldingsWorksheet(workbook, holdings) {
      const worksheet = workbook.addWorksheet('Holdings');
      
      // Default columns
      const columns = [
        { header: 'Notes', key: 'notes', width: 30 },
        { header: 'Symbol/CUSIP', key: 'symbol', width: 30 },
        { header: 'Name', key: 'name', width: 50 },
        { header: 'Quantity', key: 'qty', width: 30 },
        { header: 'Cost Basis', key: 'cost', width: 30 },
        { header: 'Market Value', key: 'value', width: 30 },
        { header: 'Fees', key: 'fees', width: 30 },
        { header: 'Security Type', key: 'type', width: 30 },
        { header: 'Account', key: 'account', width: 50 },
        { header: 'Account Registration', key: 'accountregistration', width: 50 },
        { header: 'Account Tax Status', key: 'account_tax_status', width: 50 },
      ];
      
      // Check if any holdings include purchase date
      const includePurchDate = holdings.some(asset => asset.hasOwnProperty('Purchase Date'));
      if (includePurchDate) {
        const purchaseDateColumn = { header: 'Purchase Date', key: 'purchase_date', width: 30 };
        columns.splice(4, 0, purchaseDateColumn);
      }
      
      // Apply columns to worksheet
      worksheet.columns = columns;
      
      // Populate rows
      holdings.forEach(asset => {
        let fees = asset['Fees'];
        const isPercent = typeof fees === 'string' && fees.includes('%');
        
        if (!isNaN(parseFloat(fees)) && !isPercent) {
          fees = (parseFloat(fees) * 100).toFixed(2) + '%';
        }
        
        let notes = asset['Notes'] || '';
        if (notes === undefined || notes === 'nan' || notes.toLowerCase() === 'nan') {
          notes = '';
        }
        
        let accountreg = asset['Account Registration'] || 'Unavailable';
        if (accountreg === undefined || accountreg === 'nan' || accountreg.toLowerCase() === 'nan') {
          accountreg = 'Unavailable';
        }
        
        let purchase_date = asset['Purchase Date'] || '';
        if (purchase_date === undefined || purchase_date === 'nan' || purchase_date.toLowerCase() === 'nan') {
          purchase_date = '';
        }
        
        const row = {
          notes: notes,
          symbol: asset['Symbol/CUSIP'],
          name: asset['Name'],
          cost: asset['Cost Basis'],
          purchase_date: purchase_date,
          qty: asset['Quantity'],
          value: asset['Market Value'],
          account: asset['Account'],
          accountregistration: accountreg,
          account_tax_status: asset['Account Tax Status'],
          fees: fees,
          type: asset['Security Type'],
        };
        
        // Only include purchase date in spreadsheet if there are some not blank
        if (!includePurchDate) delete row.purchase_date;
        
        worksheet.addRow(row);
      });
      
      return workbook;
    },
    
    createCustomHoldingsWorksheet(workbook, holdings) {
      // Check if the advisor has specified a custom holdings format, if not return the worksheet as is
      if (!this.advisorStore.firmAdvisorCoreSettings?.holdings_column_format?.length) {
        console.log('No custom holdings export specified');
        return workbook;
      }
      
      console.log('Custom holdings export format found');
      const worksheet = workbook.addWorksheet('Custom Holdings Format');
      
      // Create columns from the custom format
      const columns = this.advisorStore.firmAdvisorCoreSettings.holdings_column_format.map(col => ({
        header: col.custom,  // Use the custom name as header
        key: col.original.toLowerCase().replace(/[^a-z0-9]/g, ''),  // Create safe key from original
        width: 30
      }));
      
      worksheet.columns = columns;
      
      // Create a mapping for easier data access
      const columnMapping = {};
      this.advisorStore.firmAdvisorCoreSettings.holdings_column_format.forEach(col => {
        columnMapping[col.original.toLowerCase().replace(/[^a-z0-9]/g, '')] = col.original;
      });
      
      // Safely handle custom export rules
      const rules = this.advisorStore.holdings_export_rules;
      const exportRules = Array.isArray(rules) ? rules : (rules ? [rules] : []);
      
      // Apply custom export rules to holdings data
      const processedHoldings = holdings.map(asset => {
        try {
          const processedAsset = { ...asset };
          
          exportRules.forEach(rule => {
            try {
              // Validate rule structure
              if (!rule?.conditions?.length || !rule?.action?.type || !rule?.action?.target_column) {
                console.warn('Skipping invalid rule:', rule);
                return;
              }
              
              // Check if all conditions match
              const conditionsMatch = rule.conditions.every(condition => {
                try {
                  if (!condition?.column || !condition?.operator) {
                    return false;
                  }
                  
                  // Safely get value, handling undefined/null
                  const value = processedAsset[condition.column];
                  const safeValue = value === null || value === undefined ? '' : String(value);
                  
                  // Helper function for string cleaning
                  const cleanString = (str) => String(str || '').toLowerCase().trim().replace(/\.$/, '');
                  
                  switch (condition.operator) {
                    case 'contains':
                    case 'not_contains':
                      const cleanVal = cleanString(safeValue);
                      const cleanCondVal = cleanString(condition.value);
                      return condition.operator === 'contains' 
                        ? cleanVal.includes(cleanCondVal)
                        : !cleanVal.includes(cleanCondVal);
                    case 'equals':
                    case 'not_equals':
                      return condition.operator === 'equals'
                        ? cleanString(safeValue) === cleanString(condition.value)
                        : cleanString(safeValue) !== cleanString(condition.value);
                    case 'blank':
                      return !safeValue || safeValue.trim() === '';
                    case 'not_blank':
                      return safeValue && safeValue.trim() !== '';
                    default:
                      console.warn(`Unknown operator: ${condition.operator}`);
                      return false;
                  }
                } catch (conditionError) {
                  console.error('Error processing condition:', conditionError);
                  return false;
                }
              });
              
              // Apply the action if conditions match
              if (conditionsMatch) {
                switch (rule.action.type) {
                  case 'mask':
                    if (processedAsset.hasOwnProperty(rule.action.target_column)) {
                      processedAsset[rule.action.target_column] = rule.action.value;
                    }
                    break;
                  case 'transform':
                    // Future transformation logic
                    break;
                  default:
                    console.warn(`Unknown action type: ${rule.action.type}`);
                }
              }
            } catch (ruleError) {
              console.error('Error processing rule:', ruleError);
            }
          });
          return processedAsset;
        } catch (assetError) {
          console.error('Error processing asset:', assetError);
          return asset; // Return original asset if processing fails
        }
      });
      
      // Populate rows using the custom format
      processedHoldings.forEach(asset => {
        const row = {};
        Object.keys(columnMapping).forEach(key => {
          let value = asset[columnMapping[key]];
          
          // Apply the same formatting rules as in createHoldingsWorksheet
          if (columnMapping[key] === 'Fees') {
            const isPercent = typeof value === 'string' && value.includes('%');
            if (!isNaN(parseFloat(value)) && !isPercent) {
              value = (parseFloat(value) * 100).toFixed(2) + '%';
            }
          } else if (columnMapping[key] === 'Notes' && (value === undefined || value === 'nan' || value?.toLowerCase() === 'nan')) {
            value = '';
          } else if (columnMapping[key] === 'Account Registration' && (value === undefined || value === 'nan' || value?.toLowerCase() === 'nan')) {
            value = 'Unavailable';
          }
          
          row[key] = value;
        });
        
        worksheet.addRow(row);
      });
      
      return workbook;
    },
    
    createActivityWorksheet(workbook, activityData) {
      const worksheet = workbook.addWorksheet('Activity Data');
      
      // Get headers from the first row
      const headers = Object.keys(activityData[0]);
      
      // Add headers
      worksheet.addRow(headers);
      
      // Add data rows
      activityData.forEach(row => {
        worksheet.addRow(headers.map(header => row[header]));
      });
      
      return workbook;
    }
  }
};
</script>

<style scoped>
</style>